export interface ShopifyOrderLineItem {
  id: string;
  title: string;
  quantity: number;
  sku: string | null;
  price: string;
  currency: string;
  variant_id: string | null;
  product_id: string | null;
  fulfillable_quantity?: number;
}

export interface ShopifyAddress {
  name: string | null;
  company?: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  province: string | null;
  province_code?: string | null;
  country: string | null;
  country_code?: string | null;
  zip: string | null;
  phone: string | null;
}

export interface ShopifyOrder {
  id: string;
  admin_id: string;
  shopify_order_id: number;
  order_number: string;
  customer_name: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  financial_status: string;
  fulfillment_status: string | null;
  total_price: number;
  subtotal_price: number;
  total_tax: number;
  total_shipping: number;
  currency: string;
  line_items: ShopifyOrderLineItem[];
  shipping_address: ShopifyAddress | null;
  customer_note: string | null;
  order_date: string;
  synced_at: string;
  created_at: string;
  updated_at: string;
  tracking_code: string | null;
  carrier: string | null;
  fulfilled_at: string | null;
  fulfilled_by: string | null;
  printed_at: string | null;
  printed_by: string | null;
  shopify_fulfillment_id: number | null;
  is_ready: boolean;
  ready_at: string | null;
  ready_by: string | null;
  label_id: string | null;
  tracking_url: string | null;
  total_refunded: number;
  cancelled_at?: string | null;
}

export interface FinishOrderRequest {
  shopifyOrderId: number;
  notifyCustomer?: boolean;
}

export interface FinishOrderResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  trackingCode?: string;
  trackingUrl?: string;
  labelPdf?: string;
  labelId?: string;
  fulfillmentId?: number | null;
  fulfillmentError?: string | null;
  shopifyFulfilled?: boolean;
}

export interface ReprintLabelResponse {
  success: boolean;
  error?: string;
  code?: string;
  labelPdf?: string;
  trackingCode?: string;
  trackingUrl?: string;
  barcode?: string;
  printCount?: number;
  carrier?: string;
}

export interface OrderFilters {
  search: string;
  financialStatus: string;
  fulfillmentStatus: string;
  dateFrom: string;
  dateTo: string;
}

export interface OrderStats {
  totalOrders: number;
  totalRevenue: number;
  pendingOrders: number;
  fulfilledOrders: number;
  fulfillmentRate: number;
}

export type FinancialStatus =
  | "PAID"
  | "PENDING"
  | "AUTHORIZED"
  | "PARTIALLY_PAID"
  | "PARTIALLY_REFUNDED"
  | "REFUNDED"
  | "VOIDED"
  | "EXPIRED";

export type FulfillmentStatus =
  | "FULFILLED"
  | "UNFULFILLED"
  | "PARTIALLY_FULFILLED"
  | "SCHEDULED"
  | "ON_HOLD"
  | null;

export const FINANCIAL_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All Statuses" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "AUTHORIZED", label: "Authorized" },
  { value: "PARTIALLY_PAID", label: "Partially Paid" },
  { value: "PARTIALLY_REFUNDED", label: "Partially Refunded" },
  { value: "REFUNDED", label: "Refunded" },
  { value: "VOIDED", label: "Voided" },
];

export const FULFILLMENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All Statuses" },
  { value: "FULFILLED", label: "Fulfilled" },
  { value: "UNFULFILLED", label: "Unfulfilled" },
  { value: "PARTIALLY_FULFILLED", label: "Partially Fulfilled" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "ON_HOLD", label: "On Hold" },
];

export function getFinancialStatusColor(status: string): string {
  const statusMap: Record<string, string> = {
    PAID: "green",
    PENDING: "yellow",
    AUTHORIZED: "blue",
    PARTIALLY_PAID: "yellow",
    PARTIALLY_REFUNDED: "orange",
    REFUNDED: "red",
    VOIDED: "gray",
    EXPIRED: "gray",
  };
  return statusMap[status] || "gray";
}

export function getFulfillmentStatusColor(status: string | null): string {
  if (!status) return "gray";
  const statusMap: Record<string, string> = {
    FULFILLED: "green",
    UNFULFILLED: "red",
    PARTIALLY_FULFILLED: "yellow",
    SCHEDULED: "blue",
    ON_HOLD: "orange",
  };
  return statusMap[status] || "gray";
}

/** Voided / refunded / cancelled — do not treat as open fulfillment work. */
export function isTerminalFinancialStatus(financial: string | null | undefined): boolean {
  const f = (financial ?? "").trim().toUpperCase();
  return f === "VOIDED" || f === "REFUNDED" || f === "CANCELLED";
}

export function getFulfillmentDisplayLabel(
  financialStatus: string,
  fulfillmentStatus: string | null,
  cancelledAt?: string | null
): string {
  if (cancelledAt) return "Cancelled";
  const fin = financialStatus.toUpperCase();
  if (fin === "VOIDED" || fin === "CANCELLED") return "Cancelled";
  if (fin === "REFUNDED") return "Refunded";
  return fulfillmentStatus ? fulfillmentStatus.replace(/_/g, " ") : "Unfulfilled";
}

export function getFulfillmentDisplayColor(
  financialStatus: string,
  fulfillmentStatus: string | null,
  cancelledAt?: string | null
): string {
  if (cancelledAt) return "gray";
  if (isTerminalFinancialStatus(financialStatus)) return "gray";
  return getFulfillmentStatusColor(fulfillmentStatus);
}

/** True when order still needs fulfillment attention (excludes voided/cancelled financial states). */
export function isOrderAwaitingFulfillment(order: {
  financial_status: string;
  fulfillment_status: string | null;
  cancelled_at?: string | null;
}): boolean {
  if (order.cancelled_at) return false;
  if (isTerminalFinancialStatus(order.financial_status)) return false;
  return (
    !order.fulfillment_status ||
    order.fulfillment_status === "UNFULFILLED" ||
    order.fulfillment_status === "PARTIALLY_FULFILLED"
  );
}

export function formatCurrency(amount: number, currency: string = "EUR"): string {
  // Default to EUR: this is a EUR-based business, so a missing/empty currency
  // must never fall back to USD (which would render "$" on invoices/labels).
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "EUR",
  }).format(amount);
}

export function formatOrderDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return formatOrderDate(dateString);
}
